import type { PayoutTable } from './PayoutTable';
import type { SlotSymbolDef, SpinOutcome } from './types';
import type { WindowPosition, WinLine } from './WinLine';

/**
 * Lifted-in-spirit from cs-core's {@code LineSettlement}. One per payline that
 * paid out; lines that didn't match the threshold (typically <3) are omitted
 * from the result list.
 */
export interface LineSettlement {
  /** {@link WinLine} id — stable across spins for analytics. */
  lineId: string;
  /** Symbol id that anchored the match (the first non-wild on the line, or
   * the wild id if the line was entirely wild). */
  matchSymbolId: string;
  /** Count of consecutive matching positions starting from the leftmost reel. */
  matchCount: number;
  /** True if at least one wild substituted in the matched run. */
  hadWild: boolean;
  /** Coins won on this line = {@code bet * multiplier(matchSymbolId, matchCount)}. */
  winnings: number;
}

/**
 * Scatter settlement — present when {@link SettleOptions#scatterSymbolId}
 * is configured AND the spin landed at least {@code minScatterCount} of
 * that symbol anywhere on the grid (not constrained to paylines).
 *
 * <p>Lifted-in-spirit from cs-core's MMS scatter handling. Scatter wins
 * pay independently of paylines and are the canonical free-spin trigger.
 */
export interface ScatterSettlement {
  /** Total scatter symbols on the visible grid. */
  scatterCount: number;
  /** Scatter symbol id — same as {@link SettleOptions#scatterSymbolId}. */
  matchSymbolId: string;
  /** Coins paid for the scatter occurrence. {@code 0} if the payout table
   * has no entry for the scatter at this count. */
  winnings: number;
  /** Grid positions of every scatter occurrence — used by the slot machine
   * to flash the scatter cells alongside payline highlights. */
  positions: readonly WindowPosition[];
}

export interface SpinSettlement {
  totalWinnings: number;
  bet: number;
  /** Net = totalWinnings - bet. Negative = net loss for the spin. */
  net: number;
  settlements: readonly LineSettlement[];
  /** Scatter pay + trigger info; null when no scatter on the grid (or
   * scatter count fell below {@code minScatterCount}). */
  scatter: ScatterSettlement | null;
  /** Sum of {@link SlotSymbolDef#gemValue} across every visible cell
   * whose symbol declared one. Gems are a scatter-style bonus that
   * pays gems (not coins) regardless of paylines; the host credits
   * this via {@code profile.awardGems} after settle. {@code 0} when
   * no gem symbols landed on the grid. */
  gemsAwarded: number;
  /** Grid positions of every gem cell — same indices the highlight
   * loop uses, so the host can pulse + spawn a sparkle burst per
   * cell when the {@code +N 💎} toast fires. */
  gemPositions: readonly WindowPosition[];
}

export interface SettleOptions {
  outcome: SpinOutcome;
  winLines: readonly WinLine[];
  payoutTable: PayoutTable;
  bet: number;
  /** Symbol id that acts as wild (substitutes for any other). Pass {@code null}
   * if the slot has no wild. */
  wildSymbolId: string | null;
  /** Symbol id that acts as scatter — pays anywhere on the grid, doesn't
   * substitute, and is the bonus-trigger candidate. Pass {@code null} or
   * undefined for slots without a scatter. */
  scatterSymbolId?: string | null;
  /** Minimum consecutive matches required to count as a win. Default 3. */
  minMatchCount?: number;
  /** Minimum scatter occurrences for scatter to settle. Default 3. */
  minScatterCount?: number;
}

/**
 * Pure function — given a spin's visible grid and the slot's payline +
 * payout config, returns the per-line settlements, scatter settlement,
 * and the total winnings (line wins + scatter win).
 *
 * <p>Walks each payline left-to-right. The first non-wild non-scatter
 * symbol anchors the match; subsequent positions count if they match the
 * anchor or are the wild. Scatter symbols are skipped on payline walks
 * (they pay separately). If a line is entirely wild, the wild's own
 * payout entry is used. Stops counting on the first mismatch.
 *
 * <p>Scatter pays: counted across the entire grid (no payline constraint)
 * and pays from the same {@link PayoutTable} entry under the scatter id.
 * The scatter count survives in the result regardless of payout — bonus
 * triggers (free spins, bonus games) read it independently.
 */
export function settleSpin(options: SettleOptions): SpinSettlement {
  const { outcome, winLines, payoutTable, bet, wildSymbolId } = options;
  const scatterSymbolId = options.scatterSymbolId ?? null;
  const minMatchCount = options.minMatchCount ?? 3;
  const minScatterCount = options.minScatterCount ?? 3;

  const settlements: LineSettlement[] = [];

  for (const line of winLines) {
    const settlement = settleLine(
      line,
      outcome,
      payoutTable,
      bet,
      wildSymbolId,
      scatterSymbolId,
      minMatchCount,
    );
    if (settlement !== null) {
      settlements.push(settlement);
    }
  }

  const scatter = scatterSymbolId
    ? settleScatter(outcome, payoutTable, bet, scatterSymbolId, minScatterCount)
    : null;

  const { gemsAwarded, gemPositions } = settleGems(outcome);

  const linesWinnings = settlements.reduce((sum, s) => sum + s.winnings, 0);
  const scatterWinnings = scatter?.winnings ?? 0;
  const totalWinnings = linesWinnings + scatterWinnings;
  return {
    totalWinnings,
    bet,
    net: totalWinnings - bet,
    settlements,
    scatter,
    gemsAwarded,
    gemPositions,
  };
}

/** Sum {@link SlotSymbolDef#gemValue} across the visible grid and
 *  collect each gem cell's position. Local spins only: on a server spin
 *  the server pays the gem cells it lists (reel_gems), and the scene
 *  splits the grid with {@code spinGems} instead. */
export function settleGems(outcome: SpinOutcome): {
  gemsAwarded: number;
  gemPositions: WindowPosition[];
} {
  const gemPositions: WindowPosition[] = [];
  let gemsAwarded = 0;
  for (let col = 0; col < outcome.grid.length; col++) {
    const reel = outcome.grid[col]!;
    for (let row = 0; row < reel.length; row++) {
      const symbol = reel[row]!;
      const value = symbol.gemValue;
      if (value !== undefined && value > 0) {
        gemsAwarded += value;
        gemPositions.push({ col, row });
      }
    }
  }
  return { gemsAwarded, gemPositions };
}

function settleScatter(
  outcome: SpinOutcome,
  payoutTable: PayoutTable,
  bet: number,
  scatterSymbolId: string,
  minScatterCount: number,
): ScatterSettlement | null {
  const positions: WindowPosition[] = [];
  for (let col = 0; col < outcome.grid.length; col++) {
    const reel = outcome.grid[col]!;
    for (let row = 0; row < reel.length; row++) {
      if (reel[row]!.id === scatterSymbolId) {
        positions.push({ col, row });
      }
    }
  }
  if (positions.length < minScatterCount) return null;
  const multiplier = payoutTable.multiplier(scatterSymbolId, positions.length);
  return {
    scatterCount: positions.length,
    matchSymbolId: scatterSymbolId,
    winnings: bet * multiplier,
    positions,
  };
}

function settleLine(
  line: WinLine,
  outcome: SpinOutcome,
  payoutTable: PayoutTable,
  bet: number,
  wildSymbolId: string | null,
  scatterSymbolId: string | null,
  minMatchCount: number,
): LineSettlement | null {
  let anchorId: string | null = null;
  let matchCount = 0;
  let hadWild = false;

  for (const pos of line.positions) {
    const reel = outcome.grid[pos.col];
    if (!reel) {
      break;
    }
    const symbol: SlotSymbolDef | undefined = reel[pos.row];
    if (!symbol) {
      break;
    }
    // Scatter symbols never participate in payline matches — they pay
    // separately via {@link settleScatter}. Hitting one breaks the run.
    if (scatterSymbolId !== null && symbol.id === scatterSymbolId) {
      break;
    }
    // Gem-on-reel symbols pay gems independently of paylines (settled
    // by {@link settleGems}); like scatter they break the matching run
    // rather than counting as a match.
    if (symbol.gemValue !== undefined && symbol.gemValue > 0) {
      break;
    }
    // Artefact (relic) symbols are collectibles, not paytable symbols:
    // they break the run the same way (the server decides the relics).
    if (symbol.artefactId !== undefined && symbol.artefactId !== '') {
      break;
    }
    const isWild = wildSymbolId !== null && symbol.id === wildSymbolId;

    if (anchorId === null) {
      if (isWild) {
        hadWild = true;
        matchCount++;
      } else {
        anchorId = symbol.id;
        matchCount++;
      }
      continue;
    }

    if (symbol.id === anchorId || isWild) {
      matchCount++;
      if (isWild) {
        hadWild = true;
      }
    } else {
      break;
    }
  }

  if (matchCount < minMatchCount) {
    return null;
  }

  // All-wild line: pay out using the wild's own payout entry (if any).
  const matchSymbolId = anchorId ?? wildSymbolId ?? '';
  if (matchSymbolId === '') {
    return null;
  }
  const multiplier = payoutTable.multiplier(matchSymbolId, matchCount);
  if (multiplier <= 0) {
    return null;
  }
  return {
    lineId: line.id,
    matchSymbolId,
    matchCount,
    hadWild,
    winnings: bet * multiplier,
  };
}
